import React, { useState } from "react";
import { postData } from "../../services/FetchNodeservices";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Lottie from "lottie-react";
import LoginAnimate from "../../Animation/LoginAnimate.json";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [message, setMessage] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.user);

  /* ================= LOGOUT ================= */
  const handleLogout = async () => {
    try {
      if (!currentUser?.id) return;

      await postData("attendances/logout", { user_id: currentUser.id });

      dispatch({ type: "REMOVE_USER" });
      localStorage.removeItem("TOKEN");
      localStorage.removeItem("USER");

      navigate("/");
    } catch (error) {
      console.error("Logout Error:", error);
      setMessage("Error while logging out");
    }
  };

  /* ================= LOGIN ================= */
  const handleLogin = async (e) => {
    e.preventDefault();

    try {
      const response = await postData("userlogin/login", { email, password });

      if (!response?.status) {
        setIsSuccess(false);
        setMessage(response?.message || "Invalid Email or Password");
        return;
      }

      // If user is already logged in (different session), log them out first on the backend/server
      if (currentUser && currentUser.id) {
        try {
          await postData("attendances/logout", { user_id: currentUser.id });
        } catch (error) {
          console.error("Auto-logout of previous user failed:", error);
        }
      }

      const { user: newUser, token } = response;

      if (!newUser?.id) {
        setIsSuccess(false);
        setMessage("Invalid login response");
        return;
      }

      setIsSuccess(true);
      setMessage(response.message || "Login Successful!");

      localStorage.setItem("TOKEN", token);
      localStorage.setItem("USER", JSON.stringify(newUser));
      localStorage.setItem("user_id", newUser.id);
      localStorage.setItem("LOGIN_TIME", Date.now().toString());

      dispatch({
        type: "ADD_USER",
        payload: response,
      });

      await postData("attendances/punchin", {
        user_id: newUser.id,
        employee_code: newUser.employee_code,
      });

      navigate("/dashboard");
    } catch (err) {
      console.error("Login error:", err);
      setIsSuccess(false);
      setMessage("Server not reachable, try again later.");
    }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-gradient-to-tr from-indigo-50 via-white to-purple-50 p-4 relative overflow-hidden font-sans">
      {/* Background blur decorative circles */}
      <div className="absolute -top-[10%] -left-[10%] w-[35rem] h-[35rem] rounded-full bg-indigo-200/30 blur-[120px] pointer-events-none" />
      <div className="absolute -bottom-[10%] -right-[10%] w-[35rem] h-[35rem] rounded-full bg-purple-200/30 blur-[120px] pointer-events-none" />

      <div className="bg-white border border-gray-100 shadow-2xl shadow-indigo-100/40 rounded-[32px] w-full max-w-4xl overflow-hidden grid grid-cols-1 md:grid-cols-2 relative z-10 animate-fadeIn">

        {/* LEFT - FORM */}
        <div className="flex flex-col justify-center p-8 md:p-14">
          <div className="mb-8 text-center md:text-left">
            <div className="w-12 h-12 rounded-2xl bg-indigo-600 flex items-center justify-center text-white font-black text-xl shadow-lg shadow-indigo-200 mb-4 mx-auto md:mx-0">
              M
            </div>
            <h2 className="text-2xl md:text-3xl font-black text-gray-900 tracking-tight">
              Welcome back
            </h2>
            <p className="text-xs md:text-sm font-semibold text-gray-400 mt-1">
              Enter your credentials to access your Morgan portal
            </p>
          </div>

          <form onSubmit={handleLogin} className="space-y-5">
            {currentUser && (
              <div className="bg-amber-50 border border-amber-200 text-amber-800 p-3 rounded-2xl text-xs font-semibold space-y-1">
                <div>
                  ⚠️ You are currently logged in as <span className="font-extrabold">{currentUser.first_name} {currentUser.last_name}</span>.
                </div>
                <div className="text-[10px] text-amber-700 font-medium">
                  Signing in with another account will automatically log out the current session.
                </div>
              </div>
            )}

            <div>
              <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-2">Email Address</label>
              <input
                type="email"
                required
                className="w-full px-4 py-3 rounded-2xl bg-[#f4f6fa]/50 text-gray-800 border border-gray-100 focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 transition duration-200 text-sm font-medium"
                placeholder="name@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            <div>
              <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-2">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  required
                  className="w-full px-4 py-3 rounded-2xl bg-[#f4f6fa]/50 text-gray-800 border border-gray-100 focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 transition duration-200 text-sm font-medium"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <button
                  type="button"
                  className="absolute right-4 top-3.5 text-gray-400 hover:text-gray-600 transition"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {/* 🔹 RESET PASSWORD & GO TO DASHBOARD LINKS */}
            <div className="flex items-center justify-between">
              {currentUser && (
                <button
                  type="button"
                  className="text-xs text-indigo-600 font-extrabold hover:text-indigo-850 transition"
                  onClick={() => navigate("/dashboard")}
                >
                  Go to Dashboard →
                </button>
              )}
              <span
                className="text-xs text-indigo-600 font-extrabold cursor-pointer hover:text-indigo-800 transition ml-auto"
                onClick={() => navigate("/reset-password")}
              >
                Forgot Password?
              </span>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                type="submit"
                className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-3.5 rounded-2xl shadow-lg shadow-indigo-100 hover:shadow-indigo-200 transition duration-200 active:scale-[0.99]"
              >
                Sign In
              </button>
              {currentUser && (
                <button
                  type="button"
                  onClick={handleLogout}
                  className="px-6 bg-red-50 hover:bg-red-100 text-red-600 font-bold py-3.5 rounded-2xl transition"
                >
                  Logout
                </button>
              )}
            </div>
          </form>

          {message && (
            <div
              className={`mt-5 text-center text-xs p-3 rounded-2xl font-bold ${
                isSuccess 
                  ? "bg-green-50 text-green-700 border border-green-100" 
                  : "bg-red-50 text-red-600 border border-red-100"
              }`}
            >
              {message}
            </div>
          )}
        </div>

        {/* RIGHT - ANIMATION */}
        <div className="hidden md:flex items-center justify-center bg-[#f4f6fa]/40 p-8 border-l border-gray-50">
          <Lottie animationData={LoginAnimate} loop className="w-full max-w-[360px]" />
        </div>
      </div>
    </div>
  );
}
